import { translate as t } from '@/lib/i18n';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { accountsApi, ApiError } from '@/api';
import { accountKeys, analyticsKeys, schedulerKeys } from '@/lib/queryKeys';

export function useCheckNow() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => accountsApi.checkNow(id),
    onSuccess: ({ data, remaining, limit }) => {
      if (data.success) {
        const quotaMsg =
          remaining !== null && limit !== null
            ? t(`Quota restant : ${remaining}/${limit}`, `Remaining quota: ${remaining}/${limit}`)
            : remaining !== null
              ? t(`Quota restant : ${remaining}`, `Remaining quota: ${remaining}`)
              : t('Quota mis à jour', 'Quota updated');
        toast.success(
          t(
            `Scrape effectué pour ${data.reelsTouched} reel${data.reelsTouched > 1 ? 's' : ''}`,
            `Scraped ${data.reelsTouched} reel${data.reelsTouched === 1 ? '' : 's'}`,
          ),
          { description: quotaMsg },
        );
      } else {
        toast.warning(
          t('Scrape terminé avec un avertissement', 'Scrape completed with a warning'),
          {
            description:
              data.error ?? t('Voir les logs pour plus de détails', 'See logs for more details'),
          },
        );
      }
      // `accountKeys.all` couvre detail/list/dashboard/health (invalidation par préfixe).
      qc.invalidateQueries({ queryKey: accountKeys.all });
      // Un scrape met à jour les snapshots → les KPIs home/top/viral deviennent stales.
      qc.invalidateQueries({ queryKey: analyticsKeys.all });
      qc.invalidateQueries({ queryKey: schedulerKeys.all });
    },
    onError: (err) => {
      const apiErr = ApiError.from(err);
      if (apiErr.isRateLimited()) {
        const hours = apiErr.rateLimitRetryAfter
          ? Math.ceil(apiErr.rateLimitRetryAfter / 3600)
          : null;
        toast.error(t('Quota journalier épuisé', 'Daily quota exhausted'), {
          description: hours
            ? t(`Réessaie dans ${hours}h.`, `Try again in ${hours}h.`)
            : t('Réessaie plus tard.', 'Try again later.'),
        });
      } else {
        toast.error(t('Impossible de scraper ce compte', 'Unable to scrape this account'), {
          description: apiErr.localizedMessage,
        });
      }
    },
  });
}
